/**
 * EventDetailReport (FEAT-ANALYTICS) — "what does this event look like?". Pick an
 * event → occurrences/users/revenue, a volume trend, and an auto-discovered
 * profile of every property it carries (top values + share). Distinct from
 * Events (counts), Explore (fixed dimensions) and Boards (curated tiles): this is
 * zero-config, per-event parameter profiling.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Stack, SimpleGrid, Box, Flex, Text, Select, HStack } from '@chakra-ui/react';
import { useReport, useDimensionValues } from './api';
import { StatCard, StatGrid, Panel, TrendChart, ReportFrame, EmptyState, fmtInt } from './components';
import { fmtMoney } from './txnFormat';

const catsOf = (b = []) => b.map((x) => String(x || '').replace(/ 00:00:00$/, '').slice(5));

function PropertyCard({ prop }) {
  const max = Math.max(1, ...prop.values.map((v) => v.count));
  return (
    <Box bg="white" borderWidth="1px" borderColor="#ebedf0" borderRadius="12px" p={4}>
      <Flex justify="space-between" align="baseline" mb={3}>
        <Text fontSize="13px" fontWeight="700" color="gray.800" fontFamily="mono">{prop.key}</Text>
        <Text fontSize="10px" color="gray.400">{fmtInt(prop.coverage)} events</Text>
      </Flex>
      <Stack spacing={2}>
        {prop.values.map((v) => (
          <Box key={v.value}>
            <Flex justify="space-between" fontSize="12px" mb="2px">
              <Text color="gray.700" noOfLines={1} title={v.value} maxW="70%">{v.value || '(empty)'}</Text>
              <Text color="gray.500" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtInt(v.count)} · {v.share}%</Text>
            </Flex>
            <Box bg="gray.100" borderRadius="full" h="5px" overflow="hidden">
              <Box bg="var(--brand-primary)" h="100%" w={`${Math.max(2, (v.count / max) * 100)}%`} borderRadius="full" />
            </Box>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

export function EventDetailReport({ params }) {
  const { values: events, loading: eventsLoading } = useDimensionValues('event');
  const [event, setEvent] = useState('');
  // Default to the busiest event once the list loads.
  useEffect(() => { if (!event && events.length) setEvent(events[0]); }, [events, event]);

  const reportParams = useMemo(() => ({ ...params, event }), [params, event]);
  const { data, loading, error } = useReport('event-detail', event ? reportParams : null);
  const h = data?.headline || {};

  const picker = (
    <HStack>
      <Text fontSize="sm" color="gray.600">Event</Text>
      <Select size="sm" w="auto" minW="220px" bg="white" value={event} placeholder={eventsLoading ? 'loading…' : 'select event'} onChange={(e) => setEvent(e.target.value)}>
        {events.map((v) => <option key={v} value={v}>{v}</option>)}
      </Select>
    </HStack>
  );

  if (!event) {
    return (
      <Box>
        <Box mb={4}>{picker}</Box>
        <EmptyState label={eventsLoading ? 'Loading events…' : 'Pick an event to profile'} />
      </Box>
    );
  }

  return (
    <Box>
      <Box mb={4}>{picker}</Box>
      <ReportFrame loading={loading} error={error}>
        {data && (
          <Stack spacing={4}>
            <StatGrid columns={{ base: 2, md: 4 }}>
              <StatCard label="Occurrences" value={fmtInt(h.occurrences)} />
              <StatCard label="Users" value={fmtInt(h.users)} help="Distinct users firing it" />
              <StatCard label="Revenue" value={h.revenue ? fmtMoney(h.revenue) : '—'} />
              <StatCard label="Avg. value" value={h.avg_value ? fmtMoney(h.avg_value) : '—'} />
            </StatGrid>

            <Panel title={`"${event}" over time`}>
              <TrendChart categories={catsOf(data.trend?.buckets)} series={[{ name: event, data: data.trend?.values || [] }]} />
            </Panel>

            {(data.properties || []).length === 0 ? (
              <EmptyState label="This event carries no profile-worthy properties" minimal />
            ) : (
              <Box>
                <Text fontSize="xs" fontWeight="700" letterSpacing="0.06em" color="gray.500" textTransform="uppercase" mb={2}>
                  Property breakdown — {data.properties.length} attributes
                </Text>
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                  {data.properties.map((p) => <PropertyCard key={p.key} prop={p} />)}
                </SimpleGrid>
              </Box>
            )}
          </Stack>
        )}
      </ReportFrame>
    </Box>
  );
}

export default EventDetailReport;
